import { BitkitLink, BitkitSelect, BitkitSelectProps } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { forwardRef } from 'react';

import URLs from '../../enums/urls';
import { machineTypeIcon } from '../../pages/AddNewApp/stages/ProjectConfig/MachineTypeAvatar';
import {
  doesHardwareVaryByRegion,
  toMachineTypeDetailedOption,
  toNormalizedOS,
} from '../../services/StackAndMachineService';
import {
  MachineRegionId,
  MachineRegionName,
  MachineType,
  MachineTypeGroup,
  regionNames,
} from '../../types/StackAndMachine';

const DEFAULT_MACHINE_LABEL = 'Default, based on Workflow settings';

const planHelperText = (
  <Text as="span">
    Machine types for builds vary by plan. For stronger machines,{' '}
    <BitkitLink colorVariant="purple" href={URLs.DEMO_FORM}>
      contact sales
    </BitkitLink>
    .
  </Text>
);

const machineHelperText = (
  machineType: MachineType | null,
  selectedRegionId?: MachineRegionId | null,
  emptyHelperText?: string,
) => {
  if (!machineType) {
    return emptyHelperText ? (
      <Text as="span" color="text/helper" textStyle="body/sm/regular">
        {emptyHelperText}
      </Text>
    ) : null;
  }

  if (doesHardwareVaryByRegion(machineType) && !selectedRegionId) {
    return (
      <Box as="span" display="flex" flexDirection="column" gap="8">
        <Text as="span">Machine types may vary depending on high demand.</Text>
        {Object.entries(machineType.availableInRegions).map(([regionName, machineTypeInfoTexts]) => {
          return (
            <Text as="span" color="text/helper" key={regionName} textStyle="body/sm/regular">
              <Text as="span" fontWeight="bold">
                {regionName}:
              </Text>{' '}
              {(machineTypeInfoTexts ?? []).join(' • ')}
            </Text>
          );
        })}
      </Box>
    );
  }

  const regionName = selectedRegionId
    ? regionNames[selectedRegionId]
    : (Object.keys(machineType.availableInRegions)[0] as MachineRegionName);

  return (
    <Text as="span" color="text/helper" textStyle="body/sm/regular">
      {(machineType.availableInRegions[regionName] ?? []).join(' • ')}
    </Text>
  );
};

const toMachineItems = (groupedMachines: MachineTypeGroup[], region?: MachineRegionName) =>
  groupedMachines.flatMap(({ label, machineTypes }) =>
    machineTypes.map((machineType) => {
      const { isDisabled, os, subtitle, title, value } = toMachineTypeDetailedOption(machineType, region);

      return {
        disabled: isDisabled,
        // A single group carries no useful heading, so the options stay flat.
        group: groupedMachines.length > 1 ? label : undefined,
        helperText: subtitle,
        icon: machineTypeIcon(toNormalizedOS(os)),
        label: title,
        value,
      };
    }),
  );

type MachineTypeSelectorProps = Omit<BitkitSelectProps, 'helperText' | 'items' | 'label'> & {
  groupedMachines: MachineTypeGroup[];
  label: string;
  selectedMachineType: MachineType | null;
  regionId?: MachineRegionId | null;
  withDefaultOption?: boolean;
  emptyHelperText?: string;
};

// BitkitTooltip anchors itself to a ref on its child, so the ref has to reach the select.
const MachineTypeSelector = forwardRef<HTMLDivElement, MachineTypeSelectorProps>(function MachineTypeSelector(
  { groupedMachines, label, selectedMachineType, regionId, withDefaultOption = false, emptyHelperText, ...rest },
  ref,
) {
  const regionName = regionId ? regionNames[regionId] : undefined;

  return (
    <BitkitSelect
      ref={ref}
      label={label}
      optional={withDefaultOption}
      helperText={
        <Box as="span" display="flex" flexDirection="column" gap="8">
          {planHelperText}
          {machineHelperText(selectedMachineType, regionId, emptyHelperText)}
        </Box>
      }
      items={[
        ...(withDefaultOption ? [{ label: DEFAULT_MACHINE_LABEL, value: '' }] : []),
        ...toMachineItems(groupedMachines, regionName),
      ]}
      placeholder={withDefaultOption ? DEFAULT_MACHINE_LABEL : undefined}
      {...rest}
    />
  );
});

export default MachineTypeSelector;
